import { useMemo, useState } from "react";
import Button from "../../../../../shared/components/Button";
import MonthPicker from "../../../../../shared/components/MonthPicker";
import type { IWithdrawalHistory } from "../interfaces/withdrawal.interface";
import { useWithdrawalDrawerRoute } from "../hooks/useWithdrawalDrawerRoute";
import {
  buildWithdrawalHistory,
  type TransactionHistoryFilterOptions,
  type TransactionHistoryFilters,
  useCryptoWithdrawalHistory,
  useFiatWithdrawalHistory,
  mapFiatWithdrawalHistoryToTransactions,
} from "../../../services/transactions";

export default function Withdrawal() {
  const { showFundsDrawer } = useWithdrawalDrawerRoute();
  const [queryFilters, setQueryFilters] = useState<TransactionHistoryFilters>(
    () => {
      const now = new Date();
      return {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
      };
    }
  );
  const {
    data: cryptoTransactions,
    isLoading: isLoadingCrypto,
    isError: isCryptoError,
    refetch: refetchCrypto,
    isFetching: isFetchingCrypto,
  } = useCryptoWithdrawalHistory(queryFilters);

  const {
    data: fiatWithdrawalHistory,
    isLoading: isLoadingFiat,
    isError: isFiatError,
    refetch: refetchFiat,
    isFetching: isFetchingFiat,
  } = useFiatWithdrawalHistory(queryFilters);

  const fiatTransactions = useMemo(
    () => mapFiatWithdrawalHistoryToTransactions(fiatWithdrawalHistory),
    [fiatWithdrawalHistory]
  );

  const combinedTransactions = useMemo(
    () => [...(cryptoTransactions ?? []), ...(fiatTransactions ?? [])],
    [cryptoTransactions, fiatTransactions]
  );

  const isError = isCryptoError || isFiatError;
  const isLoading = isLoadingCrypto || isLoadingFiat;
  const isFetching = isFetchingCrypto || isFetchingFiat;

  const filterOptions = useMemo<
    TransactionHistoryFilterOptions | undefined
  >(() => {
    return undefined;
  }, []);

  const withdrawalHistory = useMemo<IWithdrawalHistory[]>(
    () => buildWithdrawalHistory(combinedTransactions, filterOptions),
    [combinedTransactions, filterOptions]
  );

  const handleMonthChange = (value: TransactionHistoryFilters) => {
    setQueryFilters(value);
    void Promise.all([refetchCrypto(), refetchFiat()]);
  };

  const formatTransactionId = (id: string) => {
    if (!id) return "—";
    if (id.length <= 12) return id;
    return `${id.slice(0, 6)}...${id.slice(-4)}`;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold text-white">
          Your Withdrawal History
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" className="px-4!" onClick={showFundsDrawer}>
            Withdraw Funds
          </Button>
          <MonthPicker value={queryFilters} onChange={handleMonthChange} />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-y-2">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-white/60">
              <th className="px-4 py-2">Date &amp; time</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Currency</th>
              <th className="px-4 py-2">Wallet / Bank Details</th>
              <th className="px-4 py-2">Transaction ID</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="space-y-2">
            {isError ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-sm text-red-400">
                  Unable to load your withdrawal history right now. Please try
                  again.
                </td>
              </tr>
            ) : isLoading || (isFetching && !isLoading) ? (
              Array.from({ length: 4 }).map((_, index) => (
                <tr
                  key={`withdrawal-skeleton-${index}`}
                  className="bg-[#1F242A] rounded-2xl! border border-white/10 text-sm">
                  <td className="px-4 py-3">
                    <div className="h-4 w-28 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-20 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-12 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-32 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-4 w-24 bg-white/10 rounded" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-6 w-16 bg-white/10 rounded-full" />
                  </td>
                </tr>
              ))
            ) : withdrawalHistory.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-sm text-white/60">
                  No withdrawals yet.
                </td>
              </tr>
            ) : (
              withdrawalHistory.map((item) => (
                <tr
                  key={item.id}
                  className="bg-[#1F242A] rounded-2xl! border border-white/10 text-sm text-white/80">
                  <td className="px-4 py-3 rounded-l-2xl">{item.date}</td>
                  <td className="px-4 py-3">{item.type}</td>
                  <td className="px-4 py-3">{item.currency}</td>
                  <td className="px-4 py-3">{item.walletDetail}</td>
                  <td className="px-4 py-3">
                    <span title={item.transactionId}>
                      {formatTransactionId(item.transactionId)}
                    </span>
                  </td>
                  <td className="px-4 py-3 rounded-r-2xl">
                    <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] text-white">
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
